/**
 * Integration Services
 *
 * API services for integration management.
 * Uses the shared auth pattern from api.ts (Bearer token + tenant header).
 */
import type {
  GitHubConnection,
  GitHubRepository,
  GitLabConnection,
  GitLabProject,
  JiraConnection,
  JiraIssueLink,
  AzureDevOpsConnection,
  AzureDevOpsProject,
  ServiceNowConnection,
  SnykConnection,
  SnykProject,
  NotificationChannel,
  NotificationPreference,
  CreateGitHubConnectionRequest,
  CreateGitLabConnectionRequest,
  CreateJiraConnectionRequest,
  CreateAzureDevOpsConnectionRequest,
  CreateServiceNowConnectionRequest,
  CreateSnykConnectionRequest,
  CreateNotificationChannelRequest,
  ConnectionTestResult,
  SyncResult,
  ConnectionStatus,
} from '../types';
import { API_BASE_URL, getAccessToken } from '../../../services/api';
import { getTenantIdentifier } from '../../../services/tenant/tenantAccessor';

// =============================================================================
// HTTP HELPERS (using shared auth pattern)
// =============================================================================

const INTEGRATIONS_BASE = `${API_BASE_URL}/portal/integrations`;
const NOTIFICATIONS_BASE = `${API_BASE_URL}/portal/notifications`;

async function handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    if (response.status === 404 || !isJson) {
      throw new Error('Unable to connect to integration service. Check your connection settings or contact support.');
    }
    const errorData = await response.json().catch(() => ({}));

    // Handle subscription gate 403
    if (response.status === 403 && errorData.error === 'NO_ACTIVE_SUBSCRIPTION') {
      const subscriptionError = new Error(errorData.message || 'No active subscription') as Error & { code: string };
      subscriptionError.code = 'NO_ACTIVE_SUBSCRIPTION';
      throw subscriptionError;
    }

    throw new Error(errorData.message || `HTTP error ${response.status}`);
  }

  // 204 No Content — return undefined as T
  if (response.status === 204) {
    return undefined as T;
  }

  if (!isJson) {
    throw new Error('Unable to connect to integration service. Check your connection settings or contact support.');
  }

  return response.json();
}

async function buildHeaders(includeContentType = true): Promise<HeadersInit> {
  const token = await getAccessToken();
  const tenantId = getTenantIdentifier();
  const correlationId = crypto.randomUUID();

  return {
    ...(includeContentType && { 'Content-Type': 'application/json' }),
    'X-Correlation-Id': correlationId,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(tenantId && { 'X-Tenant-Id': tenantId }),
  };
}

async function apiGet<T>(url: string): Promise<T> {
  const headers = await buildHeaders(false);
  const response = await fetch(url, { headers });
  return handleResponse<T>(response);
}

async function apiPost<T>(url: string, data?: unknown): Promise<T> {
  const headers = await buildHeaders();
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });
  return handleResponse<T>(response);
}

async function apiPatch<T>(url: string, data: unknown): Promise<T> {
  const headers = await buildHeaders();
  const response = await fetch(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data),
  });
  return handleResponse<T>(response);
}

async function apiDelete(url: string): Promise<void> {
  const headers = await buildHeaders(false);
  const response = await fetch(url, { method: 'DELETE', headers });
  if (!response.ok && response.status !== 204) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error ${response.status}`);
  }
}

// =============================================================================
// DTO MAPPING HELPERS
// =============================================================================

// Backend enum values (Newtonsoft serializes as integers by default)
const GITHUB_AUTH_TYPE_MAP: Record<string | number, GitHubConnection['installationType']> = {
  0: 'PersonalAccessToken', PersonalAccessToken: 'PersonalAccessToken',
  1: 'GitHubApp', GitHubApp: 'GitHubApp',
  2: 'OAuth2', OAuth: 'OAuth2', OAuth2: 'OAuth2',
};

const GITHUB_AUTH_TYPE_TO_BACKEND: Record<string, string> = {
  PersonalAccessToken: 'PersonalAccessToken',
  GitHubApp: 'GitHubApp',
  OAuth2: 'OAuth',
};

// Backend GitHubConnectionStatus: Connected=0, Disconnected=1, Error=2, Pending=3
// Domain GitHubConnectionStatus: Configured=0, Connected=1, Error=2, Disabled=3
// Frontend ConnectionStatus: 'Configured' | 'Connected' | 'Error' | 'Disabled'
const CONNECTION_STATUS_MAP: Record<string | number, ConnectionStatus> = {
  0: 'Connected', Connected: 'Connected',
  1: 'Configured', Disconnected: 'Configured', Configured: 'Configured',
  2: 'Error', Error: 'Error',
  3: 'Configured', Pending: 'Configured', Disabled: 'Disabled',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapGitHubConnection(dto: any): GitHubConnection {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    tenantId: dto.tenantId ?? 0,
    name: dto.name,
    installationType: GITHUB_AUTH_TYPE_MAP[dto.authType] ?? 'PersonalAccessToken',
    organizationOrUser: dto.owner ?? '',
    status: CONNECTION_STATUS_MAP[dto.status] ?? 'Configured',
    lastError: dto.lastError,
    lastTestedAt: dto.lastTestedAt,
    lastSyncAt: dto.lastSyncAt,
    webhooksEnabled: !!(dto.updateCheckRuns || dto.postCommentsOnPullRequests),
    autoScanPullRequests: dto.autoScanPullRequests ?? false,
    postCommentsOnPullRequests: dto.postCommentsOnPullRequests ?? false,
    minCommentSeverity: dto.minCommentSeverity,
    repositoryCount: dto.repositoryCount ?? 0,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapGitHubRepository(dto: any): GitHubRepository {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    connectionId: dto.connectionId ?? 0,
    gitHubRepoId: dto.gitHubRepositoryId ?? dto.gitHubRepoId ?? 0,
    name: dto.name,
    fullName: dto.fullName,
    description: dto.description,
    defaultBranch: dto.defaultBranch ?? 'main',
    htmlUrl: dto.htmlUrl ?? '',
    isPrivate: dto.isPrivate ?? false,
    isActive: dto.scanningEnabled ?? dto.isActive ?? false,
    lastScanAt: dto.lastScanAt,
    createdAt: dto.createdAt ?? dto.gitHubCreatedAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapGitLabConnection(dto: any): GitLabConnection {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    tenantId: dto.tenantId ?? 0,
    name: dto.name,
    instanceUrl: dto.instanceUrl ?? '',
    authType: dto.authType ?? 'PersonalAccessToken',
    status: CONNECTION_STATUS_MAP[dto.status] ?? 'Configured',
    lastError: dto.lastError,
    lastTestedAt: dto.lastTestedAt,
    lastSyncAt: dto.lastSyncAt,
    webhooksEnabled: dto.webhooksEnabled ?? false,
    autoScanMergeRequests: dto.autoScanMergeRequests ?? false,
    postCommentsOnMergeRequests: dto.postCommentsOnMergeRequests ?? false,
    minCommentSeverity: dto.minCommentSeverity,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapGitLabProject(dto: any): GitLabProject {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    connectionId: dto.connectionId ?? 0,
    gitLabProjectId: dto.gitLabProjectId ?? 0,
    name: dto.name,
    fullPath: dto.pathWithNamespace ?? dto.fullPath ?? '',
    description: dto.description,
    defaultBranch: dto.defaultBranch ?? 'main',
    webUrl: dto.webUrl ?? '',
    isActive: dto.scanningEnabled ?? dto.isActive ?? false,
    lastScanAt: dto.lastScanAt,
    createdAt: dto.createdAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapAzureDevOpsConnection(dto: any): AzureDevOpsConnection {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    tenantId: dto.tenantId ?? 0,
    name: dto.name,
    organizationUrl: dto.organizationUrl ?? '',
    authType: dto.authType ?? 'PersonalAccessToken',
    status: CONNECTION_STATUS_MAP[dto.status] ?? 'Configured',
    lastError: dto.lastError,
    lastTestedAt: dto.lastTestedAt,
    lastSyncAt: dto.lastSyncAt,
    webhooksEnabled: !!(dto.updatePullRequestStatus || dto.postCommentsOnPullRequests),
    autoScanPullRequests: dto.autoScanPullRequests ?? false,
    postCommentsOnPullRequests: dto.postCommentsOnPullRequests ?? false,
    minCommentSeverity: dto.minCommentSeverity,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapAzureDevOpsProject(dto: any): AzureDevOpsProject {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    connectionId: dto.connectionId ?? 0,
    azureProjectId: dto.azureDevOpsProjectId ?? dto.azureProjectId ?? '',
    name: dto.name,
    description: dto.description,
    webUrl: dto.webUrl ?? '',
    isActive: dto.scanningEnabled ?? dto.isActive ?? false,
    createdAt: dto.createdAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapJiraConnection(dto: any): JiraConnection {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    tenantId: dto.tenantId ?? 0,
    name: dto.name,
    instanceUrl: dto.instanceUrl ?? '',
    deploymentType: dto.deploymentType ?? 'Cloud',
    authType: dto.authType ?? 'ApiToken',
    username: dto.username ?? '',
    status: CONNECTION_STATUS_MAP[dto.status] ?? 'Configured',
    lastError: dto.lastError,
    lastTestedAt: dto.lastTestedAt,
    lastSyncAt: dto.lastSyncAt,
    defaultProjectKey: dto.defaultProjectKey,
    defaultIssueType: dto.defaultIssueType,
    syncDirection: dto.syncDirection ?? 'LeftShieldToJira',
    autoCreateIssues: dto.autoCreateIssues ?? false,
    minAutoCreateSeverity: dto.minAutoCreateSeverity,
    syncStatusToFindings: dto.syncStatusToFindings ?? false,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapJiraIssueLink(dto: any): JiraIssueLink {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    connectionId: dto.connectionId ?? 0,
    findingId: String(dto.findingId),
    jiraIssueKey: dto.issueKey ?? dto.jiraIssueKey ?? '',
    jiraIssueId: dto.jiraIssueId ?? '',
    jiraProjectKey: dto.jiraProjectKey ?? '',
    issueType: dto.issueType ?? '',
    summary: dto.summary ?? '',
    status: dto.jiraStatus ?? dto.status ?? '',
    priority: dto.priority,
    assignee: dto.assignee,
    webUrl: dto.issueUrl ?? dto.webUrl ?? '',
    syncStatus: dto.status === 'Error' || dto.errorMessage ? 'Error' :
      dto.lastSyncAt ? 'Synced' : 'Pending',
    lastSyncAt: dto.lastSyncAt,
    lastSyncError: dto.errorMessage ?? dto.lastSyncError,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapServiceNowConnection(dto: any): ServiceNowConnection {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    tenantId: dto.tenantId ?? 0,
    name: dto.name,
    instanceUrl: dto.instanceUrl ?? '',
    authType: dto.authType === 'OAuth' || dto.authType === 1 ? 'OAuth2' :
      dto.authType === 'BasicAuth' || dto.authType === 0 ? 'Basic' :
        (dto.authType ?? 'Basic'),
    username: dto.username ?? '',
    status: CONNECTION_STATUS_MAP[dto.status] ?? 'Configured',
    lastError: dto.lastError,
    lastTestedAt: dto.lastTestedAt,
    lastSyncAt: dto.lastSyncAt,
    defaultAssignmentGroup: dto.defaultAssignmentGroup,
    defaultCategory: dto.defaultCategory,
    autoCreateIncidents: dto.autoCreateIncidents ?? false,
    minAutoCreateSeverity: dto.minAutoCreateSeverity,
    syncStatusToFindings: dto.syncStatusToFindings ?? false,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSnykConnection(dto: any): SnykConnection {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    name: dto.name,
    organizationId: dto.organizationId ?? '',
    status: CONNECTION_STATUS_MAP[dto.status] ?? 'Configured',
    lastError: dto.lastError,
    lastTestedAt: dto.lastTestedAt,
    lastSyncAt: dto.lastSyncAt,
    projectCount: dto.projectCount ?? 0,
    autoSyncVulnerabilities: dto.autoSyncVulnerabilities ?? false,
    minImportSeverity: dto.minImportSeverity,
    createdAt: dto.createdAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSnykProject(dto: any): SnykProject {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    snykProjectId: dto.snykProjectId ?? '',
    name: dto.name,
    origin: dto.origin,
    type: dto.type ?? '',
    targetReference: dto.targetReference,
    targetFile: dto.targetFile,
    monitoringEnabled: dto.monitoringEnabled ?? false,
    isActive: dto.isActive ?? false,
    issueCountCritical: dto.issueCountCritical ?? 0,
    issueCountHigh: dto.issueCountHigh ?? 0,
    issueCountMedium: dto.issueCountMedium ?? 0,
    issueCountLow: dto.issueCountLow ?? 0,
    lastTestedAt: dto.lastTestedAt,
    lastSyncAt: dto.lastSyncAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapTestResult(dto: any): ConnectionTestResult {
  return {
    success: dto.success ?? false,
    message: dto.error ?? (dto.success ? 'Connection successful' : 'Connection failed'),
    details: {
      ...(dto.login && { login: dto.login }),
      ...(dto.name && { name: dto.name }),
      ...(dto.avatarUrl && { avatarUrl: dto.avatarUrl }),
      ...(dto.displayName && { displayName: dto.displayName }),
      ...(dto.email && { email: dto.email }),
      ...(dto.gitLabVersion && { gitLabVersion: dto.gitLabVersion }),
      ...(dto.username && { username: dto.username }),
      ...(dto.serverInfo && { serverInfo: dto.serverInfo }),
      ...(dto.userDisplayName && { userDisplayName: dto.userDisplayName }),
      ...(dto.instanceVersion && { instanceVersion: dto.instanceVersion }),
      ...(dto.organizationName && { organizationName: dto.organizationName }),
    },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSyncResult(dto: any): SyncResult {
  const errors = dto.errors ?? [];
  return {
    success: errors.length === 0,
    itemsSynced: dto.totalRepositories ?? dto.totalProjects ?? dto.totalVulnerabilities ?? 0,
    itemsFailed: errors.length,
    errors: errors.length > 0 ? errors : undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapNotificationChannel(dto: any): NotificationChannel {
  return {
    id: dto.id,
    referenceId: dto.referenceId,
    tenantId: dto.tenantId ?? 0,
    name: dto.name,
    channelType: dto.channelType ?? dto.type ?? 'Webhook',
    status: dto.status ?? 'Configured',
    isDefault: dto.isDefault ?? false,
    slackChannelName: dto.slackChannelName,
    teamsChannelName: dto.teamsChannelName,
    webhookUrl: dto.webhookUrl,
    lastError: dto.lastError,
    lastTestedAt: dto.lastTestedAt,
    lastUsedAt: dto.lastUsedAt,
    successCount: dto.successCount ?? 0,
    failureCount: dto.failureCount ?? 0,
    minimumSeverity: dto.minimumSeverity ?? 'Medium',
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

// =============================================================================
// REQUEST MAPPING HELPERS (frontend → backend)
// =============================================================================

// Map frontend NotificationChannelType to API route segments
const NOTIFICATION_CHANNEL_ROUTE_MAP: Record<string, string> = {
  Slack: 'slack',
  MicrosoftTeams: 'teams',
  Webhook: 'webhook',
  Email: 'email',
};

// Map generic CreateNotificationChannelRequest to type-specific backend DTOs
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapCreateNotificationChannelRequest(request: CreateNotificationChannelRequest): any {
  const base = { name: request.name, webhookUrl: request.webhookUrl };
  switch (request.channelType) {
    case 'Slack':
      return { ...base, channelId: request.slackChannelId, channelName: request.slackChannelName };
    case 'MicrosoftTeams':
      return { ...base, teamId: request.teamsTeamId, channelId: request.teamsChannelId, channelName: request.teamsChannelName };
    case 'Webhook':
      return { ...base, webhookSecret: request.webhookSecret };
    default:
      return base;
  }
}

function mapCreateGitHubRequest(request: CreateGitHubConnectionRequest) {
  return {
    name: request.name,
    owner: request.organizationOrUser,
    isOrganization: true,
    authType: GITHUB_AUTH_TYPE_TO_BACKEND[request.installationType] ?? request.installationType,
    accessToken: request.accessToken,
  };
}

const GITLAB_AUTH_TYPE_TO_BACKEND: Record<string, string> = {
  PersonalAccessToken: 'PersonalAccessToken',
  OAuth2: 'OAuth',
  DeployToken: 'DeployToken',
};

function mapCreateGitLabRequest(request: CreateGitLabConnectionRequest) {
  return {
    name: request.name,
    instanceUrl: request.instanceUrl,
    authType: GITLAB_AUTH_TYPE_TO_BACKEND[request.authType] ?? request.authType,
    accessToken: request.accessToken,
  };
}

const AZURE_DEVOPS_AUTH_TYPE_TO_BACKEND: Record<string, string> = {
  PersonalAccessToken: 'PersonalAccessToken',
  OAuth2: 'OAuth',
  ServicePrincipal: 'ServicePrincipal',
};

function mapCreateAzureDevOpsRequest(request: CreateAzureDevOpsConnectionRequest) {
  return {
    name: request.name,
    organizationUrl: request.organizationUrl,
    authType: AZURE_DEVOPS_AUTH_TYPE_TO_BACKEND[request.authType] ?? request.authType,
    accessToken: request.accessToken,
  };
}

const JIRA_DEPLOYMENT_TYPE_TO_BACKEND: Record<string, string> = {
  Cloud: 'Cloud',
  Server: 'Server',
  DataCenter: 'DataCenter',
};

const JIRA_AUTH_TYPE_TO_BACKEND: Record<string, string> = {
  ApiToken: 'ApiToken',
  OAuth2: 'OAuth',
  BasicAuth: 'BasicAuth',
};

function mapCreateJiraRequest(request: CreateJiraConnectionRequest) {
  return {
    name: request.name,
    instanceUrl: request.instanceUrl,
    deploymentType: JIRA_DEPLOYMENT_TYPE_TO_BACKEND[request.deploymentType] ?? request.deploymentType,
    authType: JIRA_AUTH_TYPE_TO_BACKEND[request.authType] ?? request.authType,
    username: request.username,
    apiToken: request.apiToken,
  };
}

const SERVICENOW_AUTH_TYPE_TO_BACKEND: Record<string, string> = {
  Basic: 'BasicAuth',
  OAuth2: 'OAuth',
};

function mapCreateServiceNowRequest(request: CreateServiceNowConnectionRequest) {
  return {
    name: request.name,
    instanceUrl: request.instanceUrl,
    authType: SERVICENOW_AUTH_TYPE_TO_BACKEND[request.authType] ?? request.authType,
    username: request.username,
    password: request.password,
  };
}

function mapCreateSnykRequest(request: CreateSnykConnectionRequest) {
  return {
    name: request.name,
    organizationId: request.organizationId,
    apiKey: request.apiKey,
  };
}

// =============================================================================
// INTEGRATION SERVICE
// =============================================================================

export const integrationService = {
  // GitHub
  getGitHubConnections: async (): Promise<GitHubConnection[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/github/connections`);
    return data.map(mapGitHubConnection);
  },

  getGitHubConnection: async (id: number): Promise<GitHubConnection> => {
    const data = await apiGet(`${INTEGRATIONS_BASE}/github/connections/${id}`);
    return mapGitHubConnection(data);
  },

  createGitHubConnection: async (request: CreateGitHubConnectionRequest): Promise<GitHubConnection> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/github/connections`, mapCreateGitHubRequest(request));
    return mapGitHubConnection(data);
  },

  testGitHubConnection: async (connectionId: number): Promise<ConnectionTestResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/github/connections/${connectionId}/test`);
    return mapTestResult(data);
  },

  deleteGitHubConnection: (connectionId: number) =>
    apiDelete(`${INTEGRATIONS_BASE}/github/connections/${connectionId}`),

  getGitHubRepositories: async (connectionId: number): Promise<GitHubRepository[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/github/connections/${connectionId}/repositories`);
    return data.map(mapGitHubRepository);
  },

  syncGitHubRepositories: async (connectionId: number): Promise<SyncResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/github/connections/${connectionId}/sync`);
    return mapSyncResult(data);
  },

  setGitHubRepositoryScanning: (repositoryId: number, enabled: boolean) =>
    apiPatch<void>(`${INTEGRATIONS_BASE}/github/repositories/${repositoryId}/scanning`, { enabled }),

  // GitLab
  getGitLabConnections: async (): Promise<GitLabConnection[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/gitlab/connections`);
    return data.map(mapGitLabConnection);
  },

  getGitLabConnection: async (id: number): Promise<GitLabConnection> => {
    const data = await apiGet(`${INTEGRATIONS_BASE}/gitlab/connections/${id}`);
    return mapGitLabConnection(data);
  },

  createGitLabConnection: async (request: CreateGitLabConnectionRequest): Promise<GitLabConnection> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/gitlab/connections`, mapCreateGitLabRequest(request));
    return mapGitLabConnection(data);
  },

  testGitLabConnection: async (connectionId: number): Promise<ConnectionTestResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/gitlab/connections/${connectionId}/test`);
    return mapTestResult(data);
  },

  deleteGitLabConnection: (connectionId: number) =>
    apiDelete(`${INTEGRATIONS_BASE}/gitlab/connections/${connectionId}`),

  getGitLabProjects: async (connectionId: number): Promise<GitLabProject[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/gitlab/connections/${connectionId}/projects`);
    return data.map(mapGitLabProject);
  },

  syncGitLabProjects: async (connectionId: number): Promise<SyncResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/gitlab/connections/${connectionId}/sync`);
    return mapSyncResult(data);
  },

  setGitLabProjectScanning: (projectId: number, enabled: boolean) =>
    apiPatch<void>(`${INTEGRATIONS_BASE}/gitlab/projects/${projectId}/scanning`, { enabled }),

  // Azure DevOps
  getAzureDevOpsConnections: async (): Promise<AzureDevOpsConnection[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/azure-devops/connections`);
    return data.map(mapAzureDevOpsConnection);
  },

  getAzureDevOpsConnection: async (id: number): Promise<AzureDevOpsConnection> => {
    const data = await apiGet(`${INTEGRATIONS_BASE}/azure-devops/connections/${id}`);
    return mapAzureDevOpsConnection(data);
  },

  createAzureDevOpsConnection: async (request: CreateAzureDevOpsConnectionRequest): Promise<AzureDevOpsConnection> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/azure-devops/connections`, mapCreateAzureDevOpsRequest(request));
    return mapAzureDevOpsConnection(data);
  },

  testAzureDevOpsConnection: async (connectionId: number): Promise<ConnectionTestResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/azure-devops/connections/${connectionId}/test`);
    return mapTestResult(data);
  },

  deleteAzureDevOpsConnection: (connectionId: number) =>
    apiDelete(`${INTEGRATIONS_BASE}/azure-devops/connections/${connectionId}`),

  getAzureDevOpsProjects: async (connectionId: number): Promise<AzureDevOpsProject[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/azure-devops/connections/${connectionId}/projects`);
    return data.map(mapAzureDevOpsProject);
  },

  syncAzureDevOpsProjects: async (connectionId: number): Promise<SyncResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/azure-devops/connections/${connectionId}/sync`);
    return mapSyncResult(data);
  },

  // Jira
  getJiraConnections: async (): Promise<JiraConnection[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/jira/connections`);
    return data.map(mapJiraConnection);
  },

  getJiraConnection: async (id: number): Promise<JiraConnection> => {
    const data = await apiGet(`${INTEGRATIONS_BASE}/jira/connections/${id}`);
    return mapJiraConnection(data);
  },

  createJiraConnection: async (request: CreateJiraConnectionRequest): Promise<JiraConnection> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/jira/connections`, mapCreateJiraRequest(request));
    return mapJiraConnection(data);
  },

  testJiraConnection: async (connectionId: number): Promise<ConnectionTestResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/jira/connections/${connectionId}/test`);
    return mapTestResult(data);
  },

  deleteJiraConnection: (connectionId: number) =>
    apiDelete(`${INTEGRATIONS_BASE}/jira/connections/${connectionId}`),

  getJiraIssueLinks: async (connectionId: number): Promise<JiraIssueLink[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/jira/connections/${connectionId}/issue-links`);
    return data.map(mapJiraIssueLink);
  },

  createJiraIssue: async (connectionId: number, findingId: string, projectKey: string, issueType: string): Promise<JiraIssueLink> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/jira/connections/${connectionId}/issues`, {
      findingId: Number(findingId) || findingId,
      projectKey,
      issueType,
      summary: `LeftShield Finding: ${findingId}`,
    });
    return mapJiraIssueLink(data);
  },

  // ServiceNow
  getServiceNowConnections: async (): Promise<ServiceNowConnection[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/servicenow/connections`);
    return data.map(mapServiceNowConnection);
  },

  getServiceNowConnection: async (id: number): Promise<ServiceNowConnection> => {
    const data = await apiGet(`${INTEGRATIONS_BASE}/servicenow/connections/${id}`);
    return mapServiceNowConnection(data);
  },

  createServiceNowConnection: async (request: CreateServiceNowConnectionRequest): Promise<ServiceNowConnection> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/servicenow/connections`, mapCreateServiceNowRequest(request));
    return mapServiceNowConnection(data);
  },

  testServiceNowConnection: async (connectionId: number): Promise<ConnectionTestResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/servicenow/connections/${connectionId}/test`);
    return mapTestResult(data);
  },

  deleteServiceNowConnection: (connectionId: number) =>
    apiDelete(`${INTEGRATIONS_BASE}/servicenow/connections/${connectionId}`),

  // Snyk
  getSnykConnections: async (): Promise<SnykConnection[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/snyk/connections`);
    return data.map(mapSnykConnection);
  },

  getSnykConnection: async (id: number): Promise<SnykConnection> => {
    const data = await apiGet(`${INTEGRATIONS_BASE}/snyk/connections/${id}`);
    return mapSnykConnection(data);
  },

  createSnykConnection: async (request: CreateSnykConnectionRequest): Promise<SnykConnection> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/snyk/connections`, mapCreateSnykRequest(request));
    return mapSnykConnection(data);
  },

  testSnykConnection: async (connectionId: number): Promise<ConnectionTestResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/snyk/connections/${connectionId}/test`);
    return mapTestResult(data);
  },

  deleteSnykConnection: (connectionId: number) =>
    apiDelete(`${INTEGRATIONS_BASE}/snyk/connections/${connectionId}`),

  getSnykProjects: async (connectionId: number): Promise<SnykProject[]> => {
    const data = await apiGet<unknown[]>(`${INTEGRATIONS_BASE}/snyk/connections/${connectionId}/projects`);
    return data.map(mapSnykProject);
  },

  syncSnykProjects: async (connectionId: number): Promise<SyncResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/snyk/connections/${connectionId}/sync-projects`);
    return mapSyncResult(data);
  },

  syncSnykVulnerabilities: async (connectionId: number): Promise<SyncResult> => {
    const data = await apiPost(`${INTEGRATIONS_BASE}/snyk/connections/${connectionId}/sync-vulnerabilities`);
    return mapSyncResult(data);
  },

  // Notifications
  getNotificationChannels: async (): Promise<NotificationChannel[]> => {
    const data = await apiGet<unknown[]>(`${NOTIFICATIONS_BASE}/channels`);
    return data.map(mapNotificationChannel);
  },

  getNotificationChannel: async (id: number): Promise<NotificationChannel> => {
    const data = await apiGet(`${NOTIFICATIONS_BASE}/channels/${id}`);
    return mapNotificationChannel(data);
  },

  createNotificationChannel: async (request: CreateNotificationChannelRequest): Promise<NotificationChannel> => {
    const routeSegment = NOTIFICATION_CHANNEL_ROUTE_MAP[request.channelType] ?? request.channelType.toLowerCase();
    const data = await apiPost(`${NOTIFICATIONS_BASE}/channels/${routeSegment}`, mapCreateNotificationChannelRequest(request));
    return mapNotificationChannel(data);
  },

  testNotificationChannel: async (channelId: number): Promise<ConnectionTestResult> => {
    const data = await apiPost(`${NOTIFICATIONS_BASE}/channels/${channelId}/test`);
    return mapTestResult(data);
  },

  deleteNotificationChannel: (channelId: number) =>
    apiDelete(`${NOTIFICATIONS_BASE}/channels/${channelId}`),

  getNotificationPreferences: () =>
    apiGet<NotificationPreference[]>(`${NOTIFICATIONS_BASE}/preferences`),

  updateNotificationPreference: (preference: Partial<NotificationPreference> & { id: number }) =>
    apiPatch<NotificationPreference>(`${NOTIFICATIONS_BASE}/preferences`, preference),
};

export default integrationService;
